import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { Copy, Pencil, Plus, X } from "lucide-react";
import { SectionItem, SectionWithItem } from "@reactive-resume/schema";
import { useBreakpoint } from "@reactive-resume/hooks";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Form,
} from "@reactive-resume/ui";
import { AnimatePresence, motion } from "framer-motion";
import { produce } from "immer";
import get from "lodash.get";
import { useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";

import { DialogName, useDialog } from "@/client/stores/dialog";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

type Props<T extends SectionItem> = {
  id: DialogName;
  form: UseFormReturn<T>;
  defaultValues: T;
  children: React.ReactNode;
};

export const SectionDialog = <T extends SectionItem>({
  id,
  form,
  defaultValues,
  children,
}: Props<T>) => {
  const { isOpen, mode, close, payload } = useDialog<T>(id);
  const { isDesktop } = useBreakpoint();

  // Size the inline editor to match the left editor column (falls back to 38%).
  const leftSize = useBuilderStore((state) => state.panel.left.size) || 38;

  const setValue = useResumeStore((state) => state.setValue);
  const setPreviewOverlay = useResumeStore((state) => state.setPreviewOverlay);
  const clearPreviewOverlay = useResumeStore((state) => state.clearPreviewOverlay);
  const section = useResumeStore((state) => {
    if (!id) return null;
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  // Live form values, so edits reflect in the preview as the user types.
  const watched = useWatch({ control: form.control });

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";
  const isForm = isCreate || isUpdate || isDuplicate;

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen, payload]);

  // Close the inline editor with Escape (Radix handled this for the modal).
  useEffect(() => {
    if (!isOpen || !isForm) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isForm, close]);

  // Live preview: project the in-progress form values onto the resume preview
  // without committing them, so the change is visible while editing. The overlay
  // is non-destructive and is discarded on Cancel/close; Save commits for real.
  useEffect(() => {
    if (!isOpen || !isForm || !section) return;

    const items = (section.items as any[]) ?? [];
    const draft = { ...(watched as any) };
    const previewItems =
      isUpdate && payload.item?.id
        ? items.map((item) => (item.id === payload.item?.id ? { ...item, ...draft } : item))
        : [...items, draft];

    setPreviewOverlay(`sections.${id}.items`, previewItems);
  }, [watched, isOpen, isForm, isUpdate, section, id, payload, setPreviewOverlay]);

  // Drop the overlay as soon as the editor closes (Cancel, Save, or Escape).
  useEffect(() => {
    if (!isOpen) clearPreviewOverlay();
    return () => clearPreviewOverlay();
  }, [isOpen, clearPreviewOverlay]);

  const onSubmit = async (values: T) => {
    if (!section) return;

    if (isCreate || isDuplicate) {
      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          draft.push({ ...values, id: createId() });
        }),
      );
    }

    if (isUpdate) {
      if (!payload.item?.id) return;

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === payload.item?.id);
          if (index === -1) return;
          draft[index] = values;
        }),
      );
    }

    if (isDelete) {
      if (!payload.item?.id) return;

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === payload.item?.id);
          if (index === -1) return;
          draft.splice(index, 1);
        }),
      );
    }

    close();
  };

  const onReset = () => {
    if (isCreate) form.reset({ ...defaultValues, id: createId() } as T);
    if (isUpdate) form.reset({ ...defaultValues, ...payload.item });
    if (isDuplicate) form.reset({ ...payload.item, id: createId() } as T);
    if (isDelete) form.reset({ ...defaultValues, ...payload.item });
  };

  // Deletion stays a small confirmation modal — it's a one-tap decision.
  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent className="z-50">
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete this item?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action can be reverted by clicking on the undo button in the floating toolbar.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Create / Update / Duplicate render as an inline panel docked over the editor
  // column, so the resume preview on the right stays visible while editing.
  return (
    <AnimatePresence>
      {isOpen && isForm && (
        <motion.div
          className="fixed inset-y-0 left-0 z-[55] flex flex-col border-r border-border bg-background shadow-2xl"
          style={{ width: isDesktop ? `${leftSize}%` : "100%" }}
          initial={{ x: "-100%" }}
          animate={{ x: 0 }}
          exit={{ x: "-100%" }}
          transition={{ type: "tween", duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
        >
          <Form {...form}>
            <form className="flex h-full flex-col" onSubmit={form.handleSubmit(onSubmit)}>
              <header className="flex items-center justify-between border-b border-border px-5 py-4">
                <div className="flex items-center gap-x-2.5">
                  {isCreate && <Plus size={18} className="text-primary" />}
                  {isUpdate && <Pencil size={18} className="text-primary" />}
                  {isDuplicate && <Copy size={18} className="text-primary" />}
                  <h2 className="text-base font-semibold tracking-tight">
                    {isCreate && t`Create a new item`}
                    {isUpdate && t`Edit item`}
                    {isDuplicate && t`Duplicate item`}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={close}
                  aria-label={t`Close`}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
                >
                  <X size={18} />
                </button>
              </header>

              <div className="flex-1 overflow-auto px-5 py-5">{children}</div>

              <footer className="flex items-center justify-end gap-x-2 border-t border-border px-5 py-4">
                <Button type="button" variant="outline" onClick={close}>
                  {t`Cancel`}
                </Button>
                <Button type="submit">
                  {isCreate && t`Create`}
                  {isUpdate && t`Save Changes`}
                  {isDuplicate && t`Duplicate`}
                </Button>
              </footer>
            </form>
          </Form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
